import React, { CSSProperties, ReactNode, useEffect } from 'react';
import * as t from 'io-ts';
import { usePageContext } from '@narvar/nth-hook-context-page';
import { useTheme } from '@narvar/nth-hook-theme';
import { RetailerTheme } from '@narvar/nth-hook-loma';
import { useFormContext, useWatch } from 'react-hook-form';
import RegisteredCheckbox from '../form-fields/RegisteredCheckbox';
import RegisteredInput from '../form-fields/RegisteredInput';
import { FormColumn, FormRow } from '../form-layout';
import { optional } from '../../../server/lib/codec';
import validateUserProvidedEmail from './api';
import { useTitanAnalytics } from '../../hooks/useTitanAnalytics';
import { useSublimeDeliveryInstructions } from '../../hooks/useSublimeDeliveryInstructions';

const InputNames = {
  sublimeOptin: 'sublimeOptin',
  sublimeEmail: 'sublimeEmail',
} as const;

export const sublimeOptionFieldValuesV = t.strict(
  {
    [InputNames.sublimeOptin]: optional(t.boolean),
    [InputNames.sublimeEmail]: optional(t.string),
  },
  'sublimeOptionFieldValuesV',
);

export type SublimeOptionFieldValues = t.TypeOf<
  typeof sublimeOptionFieldValuesV
>;

const DeliveryInstructionsSublimeOptionImpl = () => {
  const {
    control,
    formState: { isSubmitting, errors },
    register,
    resetField,
  } = useFormContext<SublimeOptionFieldValues>();

  const { state: pageContext } = usePageContext();
  const { retailerMoniker, trackingNumbers, orderNumbers, carrier } =
    pageContext;

  const { bodytextDefaultNone, linksTrackingDefault } = useTheme() as {
    [key in keyof RetailerTheme]: CSSProperties;
  };

  const optinInputValue = useWatch({
    control,
    name: InputNames.sublimeOptin,
  });

  const emailInputValue = useWatch({
    control,
    name: InputNames.sublimeEmail,
  });

  const titan = useTitanAnalytics();

  useEffect(() => {
    if (!titan) return;

    titan
      .track({
        event: 'promotion_viewed',
        channel: 'web',
        type: 'track',
        context: {},
        properties: {
          sku: 'delivery-preferences',
          variant: 'unsubscribed',
          narvar_product_context: 'track',
          extra_properties: [
            {
              key: 'carrier',
              value: carrier,
            },
            {
              key: 'orderId',
              value: orderNumbers?.[0],
            },
            {
              key: 'trackingNumber',
              value: trackingNumbers?.[0],
            },
          ],
        },
      })
      // eslint-disable-next-line no-console
      .catch((error) => console.error(error));
  }, [titan, orderNumbers, trackingNumbers, carrier]);

  useEffect(() => {
    if (optinInputValue) return;
    resetField(InputNames.sublimeEmail);
  }, [optinInputValue, resetField]);

  // Temporary Sublime Text
  const deliveryInstructionsSublimeOption =
    'Save your delivery preferences to use for future orders with retailers who use Narvar. You can add, update or delete your preferences.';
  const deliveryInstructionsSublimeOptionLearnMore = 'Learn more';
  const deliveryInstructionsSublimeConfirm =
    'Confirm your email address associated with this order';
  const deliveryInstructionsSublimePlaceholder = 'Email Address';
  const deliveryInstructionsEmailFieldErrorText =
    'Please enter a valid email address';
  const deliveryInstructionsRequiredFieldErrorText =
    'Please fill in required field';
  const deliveryInstructionsSublimeInvalidEmail =
    'Unable to validate your email address. Please try again or submit without saving your instructions.';

  const validateEmail = async (email: string | undefined) => {
    const trackingNumber = trackingNumbers?.[0];
    const orderNumber = orderNumbers?.[0];

    if (!isSubmitting) return true;

    if (
      trackingNumber?.toLowerCase() === 'preview_justshipped' ||
      trackingNumber?.toLowerCase() === 'preview_intransit'
    )
      return true;

    if (
      !email ||
      !trackingNumber ||
      !orderNumber ||
      !carrier ||
      !retailerMoniker
    )
      return deliveryInstructionsSublimeInvalidEmail;

    const validEmail = await validateUserProvidedEmail({
      email,
      trackingNumber,
      orderNumber,
      carrier,
      retailer: retailerMoniker,
    });

    return validEmail || deliveryInstructionsSublimeInvalidEmail;
  };

  return (
    <>
      <FormRow>
        <FormColumn>
          <label htmlFor={InputNames.sublimeOptin}>
            <div style={{ display: 'flex' }}>
              <div style={{ paddingRight: '0.5rem' }}>
                <RegisteredCheckbox
                  id={InputNames.sublimeOptin}
                  register={register(InputNames.sublimeOptin)}
                  checked={optinInputValue}
                >
                  <span style={{ ...bodytextDefaultNone }}>
                    {deliveryInstructionsSublimeOption}{' '}
                    <a
                      href="https://profile.narvar.com"
                      target="_blank"
                      rel="noreferrer"
                      style={{ ...linksTrackingDefault }}
                    >
                      {deliveryInstructionsSublimeOptionLearnMore}
                    </a>
                    .
                  </span>
                </RegisteredCheckbox>
              </div>
            </div>
          </label>
        </FormColumn>
      </FormRow>
      {optinInputValue && (
        <FormRow>
          <FormColumn>
            <span
              style={{
                ...bodytextDefaultNone,
                display: 'inline-block',
                marginBottom: '0.25rem',
              }}
            >
              {deliveryInstructionsSublimeConfirm}
            </span>
            <RegisteredInput
              id={InputNames.sublimeEmail}
              register={register(InputNames.sublimeEmail, {
                shouldUnregister: true,
                required: {
                  value: true,
                  message: deliveryInstructionsRequiredFieldErrorText,
                },
                pattern: {
                  value: /^\S+@\S+\.\S+$/,
                  message: deliveryInstructionsEmailFieldErrorText,
                },
                validate: {
                  validateEmail,
                },
              })}
              errorMessage={errors.sublimeEmail && errors.sublimeEmail.message}
              value={emailInputValue}
              label={deliveryInstructionsSublimePlaceholder}
              animateLabel
            />
          </FormColumn>
        </FormRow>
      )}
    </>
  );
};

const IfSublimeDeliveryInstructionsEnabled = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isEnabled } = useSublimeDeliveryInstructions();

  // children is opaque and this is the safest way to handle any format
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return isEnabled ? <>{children}</> : null;
};

const DeliveryInstructionsSublimeOption = () => (
  <IfSublimeDeliveryInstructionsEnabled>
    <DeliveryInstructionsSublimeOptionImpl />
  </IfSublimeDeliveryInstructionsEnabled>
);

export default DeliveryInstructionsSublimeOption;
